// Whiteboard engine — elements: making them, where they are, what a click
// hits, how they move / resize / rotate, and what they look like (as
// "primitives" every renderer draws the same way: SVG on screen, Canvas for
// PNG, jsPDF for vector PDF).
//
// An element: { id, type, x, y, w, h, angle, z, stroke, fill, width, dash,
// opacity, … } with points RELATIVE to (x, y) for pen / highlighter / line /
// arrow, text props for text / note / shapes with words, fileId for images.
// It rotates about its box centre. Every change makes a NEW object with the
// version bumped (stamp) — the merge rule is the server's (model.js).

import { rotate, boxOfPoints, corners, unionBox, distToSegment, pointInPolygon, segmentDistance, normAngle, simplify as simplifyPts } from './geometry.js';
import { strokeOutline, smoothClosed, smoothOpen } from './freehand.js';
import { layoutText, baselineOffset } from './text.js';
import { symbolPrims, symbolSize, dimensionBox, layoutSymbol, leaderStart, DEFAULT_PARAMS } from './symbols.js';

export const LINEAR = new Set(['pen', 'highlighter', 'line', 'arrow']);
export const SHAPES = new Set(['rect', 'ellipse', 'diamond', 'triangle']);
export const HAS_TEXT = new Set(['rect', 'ellipse', 'diamond', 'triangle', 'note', 'text']);
const K = 0.5522847498;

// ---- ids, versions ---------------------------------------------------------------
const ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
export function newId(len = 16) {
  let out = '';
  const bytes = new Uint8Array(len);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < len; i++) bytes[i] = Math.floor(Math.random() * 256);
  for (let i = 0; i < len; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return out;
}
export const newNonce = () => Math.floor(Math.random() * 2 ** 31);
// A changed element: the next version, a fresh nonce.
export function stamp(el, prev) {
  return { ...el, version: Math.max(el.version || 0, prev?.version || 0) + 1, nonce: newNonce(), updated: Date.now() };
}
// Does `a` replace `b`? The server's rule exactly (higher version; lower nonce on a tie).
export function wins(a, b) {
  if (!b) return true;
  if (a.version !== b.version) return a.version > b.version;
  return a.nonce < b.nonce;
}

// ---- geometry of an element ---------------------------------------------------------
export const center = (el) => [el.x + el.w / 2, el.y + el.h / 2];
export const localBox = (el) => ({ x: el.x, y: el.y, w: el.w, h: el.h });
const isDim = (el) => el.type === 'symbol' && el.kind === 'dimension';
// The local box of what is drawn: the element's own box — for a dimension,
// its lines and words (its x, y, w is the measured span).
export function bodyBox(el) { return isDim(el) ? dimensionBox(el) : localBox(el); }
// …and with an annotation's leader included.
export function extentOf(el) {
  let b = bodyBox(el);
  if (el.type === 'symbol' && el.leader) b = unionBox(b, { x: el.x + el.leader[0], y: el.y + el.leader[1], w: 0, h: 0 });
  return b;
}

// How far the ink reaches beyond the box (stroke width, arrowheads).
function inkPad(el) {
  if (el.type === 'pen') return el.width * 0.9 + 1;
  if (el.type === 'highlighter') return el.width / 2 + 1;
  if (el.type === 'line' || el.type === 'arrow') {
    const heads = (el.startHead && el.startHead !== 'none') || (el.endHead && el.endHead !== 'none');
    return el.width / 2 + (heads ? headSize(el) : 0) + 1;
  }
  if (el.type === 'note') return 6;
  if (el.type === 'symbol') return el.fontSize * 0.6;
  if (el.type === 'text' || el.type === 'image') return 0;
  return el.stroke === 'transparent' ? 0 : el.width / 2;
}

// The axis-aligned box the element covers on the board (rotation and ink included).
export function bounds(el) {
  const pad = inkPad(el);
  const e = extentOf(el);
  const b = { x: e.x - pad, y: e.y - pad, w: e.w + pad * 2, h: e.h + pad * 2 };
  if (!el.angle) return b;
  const [cx, cy] = center(el);
  return boxOfPoints(corners(b).map(([x, y]) => rotate(x, y, cx, cy, el.angle)));
}
export function boundsOf(list) {
  let b = null;
  for (const el of list) b = unionBox(b, bounds(el));
  return b;
}

// A board point → the element's own unrotated frame.
export function toLocal(el, x, y) {
  if (!el.angle) return [x, y];
  const [cx, cy] = center(el);
  return rotate(x, y, cx, cy, -el.angle);
}
export function toWorld(el, x, y) {
  if (!el.angle) return [x, y];
  const [cx, cy] = center(el);
  return rotate(x, y, cx, cy, el.angle);
}
// The points of a linear element in the board's frame.
export function worldPoints(el) {
  return (el.points || []).map((p) => toWorld(el, el.x + p[0], el.y + p[1]));
}

// The outline of a shape in its own frame (absolute coords) — hit tests and the eraser.
function outlinePolygon(el) {
  const { x, y, w, h } = el;
  switch (el.type) {
    case 'diamond': return [[x + w / 2, y], [x + w, y + h / 2], [x + w / 2, y + h], [x, y + h / 2]];
    case 'triangle': return [[x + w / 2, y], [x + w, y + h], [x, y + h]];
    case 'ellipse': {
      const out = [];
      for (let i = 0; i < 48; i++) {
        const a = (i / 48) * Math.PI * 2;
        out.push([x + w / 2 + (Math.cos(a) * w) / 2, y + h / 2 + (Math.sin(a) * h) / 2]);
      }
      return out;
    }
    default: return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
  }
}
const isSolid = (el) => el.type === 'note' || el.type === 'image' || el.type === 'text'
  || (el.fill && el.fill !== 'transparent');

function nearPolyline(pts, x, y, r) {
  if (pts.length === 1) return Math.hypot(pts[0][0] - x, pts[0][1] - y) <= r;
  for (let i = 1; i < pts.length; i++) {
    if (distToSegment(x, y, pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1]) <= r) return true;
  }
  return false;
}
function nearOutline(poly, x, y, r) {
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]; const b = poly[(i + 1) % poly.length];
    if (distToSegment(x, y, a[0], a[1], b[0], b[1]) <= r) return true;
  }
  return false;
}

// Does a click at board (x, y), with tol board units of slack, land on el?
export function hits(el, x, y, tol) {
  const [lx, ly] = toLocal(el, x, y);
  if (LINEAR.has(el.type)) {
    const pts = el.points.map((p) => [el.x + p[0], el.y + p[1]]);
    const r = (el.type === 'pen' ? el.width * 0.8 : el.width / 2) + tol;
    return nearPolyline(pts, lx, ly, r);
  }
  if (el.type === 'symbol') {
    const b = bodyBox(el);
    if (lx >= b.x - tol && lx <= b.x + b.w + tol && ly >= b.y - tol && ly <= b.y + b.h + tol) return true;
    if (el.leader && !isDim(el)) {
      const st = leaderStart(el, layoutSymbol(el));
      return distToSegment(lx, ly, st[0], st[1], el.x + el.leader[0], el.y + el.leader[1]) <= tol + el.fontSize * 0.35;
    }
    return false;
  }
  const poly = outlinePolygon(el);
  if (isSolid(el)) {
    if (pointInPolygon(lx, ly, poly)) return true;
    return nearOutline(poly, lx, ly, tol);
  }
  if (nearOutline(poly, lx, ly, el.width / 2 + tol)) return true;
  // a hollow shape with words: the words are a handle too
  if (el.text) {
    const t = textBox(el);
    return lx >= t.x && lx <= t.x + t.w && ly >= t.y && ly <= t.y + t.h;
  }
  return false;
}

// Does the eraser's stroke from a to b (radius r) touch el?
export function touchesSegment(el, a, b, r) {
  const la = toLocal(el, a[0], a[1]); const lb = toLocal(el, b[0], b[1]);
  if (LINEAR.has(el.type)) {
    const pts = el.points.map((p) => [el.x + p[0], el.y + p[1]]);
    const rr = r + (el.type === 'pen' ? el.width * 0.7 : el.width / 2);
    if (pts.length === 1) return distToSegment(pts[0][0], pts[0][1], la[0], la[1], lb[0], lb[1]) <= rr;
    for (let i = 1; i < pts.length; i++) if (segmentDistance(pts[i - 1], pts[i], la, lb) <= rr) return true;
    return false;
  }
  const poly = el.type === 'symbol' ? corners(extentOf(el)) : outlinePolygon(el);
  for (let i = 0; i < poly.length; i++) {
    if (segmentDistance(poly[i], poly[(i + 1) % poly.length], la, lb) <= r + el.width / 2) return true;
  }
  return (isSolid(el) || el.type === 'symbol') && (pointInPolygon(la[0], la[1], poly) || pointInPolygon(lb[0], lb[1], poly));
}

// Inside a lasso (a closed polygon of board points)? Most of it must be.
export function insideLasso(el, poly) {
  const sample = LINEAR.has(el.type)
    ? worldPoints(el).filter((_, i, all) => all.length < 60 || i % Math.ceil(all.length / 60) === 0)
    : [...corners(extentOf(el)).map(([x, y]) => toWorld(el, x, y)), center(el)];
  if (!sample.length) return false;
  const inside = sample.filter(([x, y]) => pointInPolygon(x, y, poly)).length;
  return inside / sample.length >= 0.8;
}

// ---- making elements ----------------------------------------------------------------
export const PALETTE = ['#1e293b', '#64748b', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#7c3aed', '#db2777'];
export const NOTE_COLORS = ['#fef08a', '#fed7aa', '#fecaca', '#bbf7d0', '#bae6fd', '#ddd6fe', '#e2e8f0'];

export function makeElement(type, props, style = {}) {
  const base = {
    id: newId(), type, x: 0, y: 0, w: 0, h: 0, angle: 0, z: 0,
    stroke: style.stroke || '#1e293b', fill: 'transparent', width: style.width || 3,
    dash: style.dash || 'solid', opacity: style.opacity ?? 1,
    version: 0, nonce: newNonce(), updated: Date.now(),
  };
  const el = { ...base, ...props };
  if (type === 'highlighter') { el.stroke = style.highlight || '#facc15'; el.width = style.highlightWidth || 18; el.opacity = 0.4; el.dash = 'solid'; }
  if (SHAPES.has(type)) el.fill = style.fill || 'transparent';
  if (type === 'line' || type === 'arrow') {
    el.startHead = style.startHead || 'none';
    el.endHead = type === 'arrow' ? (style.endHead && style.endHead !== 'none' ? style.endHead : 'arrow') : 'none';
  }
  if (HAS_TEXT.has(type)) {
    el.text = props.text || '';
    el.fontSize = style.fontSize || 24;
    el.font = style.font || 'sans';
    el.align = type === 'text' ? (style.align || 'left') : 'center';
    el.textColor = type === 'note' ? '#1e293b' : (style.stroke || '#1e293b');
    if (style.bold) el.bold = true;
  }
  if (type === 'note') {
    el.fill = style.noteColor || NOTE_COLORS[0];
    el.stroke = 'transparent'; el.width = 1; el.dash = 'solid';
    el.w = el.w || 220; el.h = el.h || 220;
  }
  if (type === 'text') { el.stroke = 'transparent'; el.fill = 'transparent'; }
  if (type === 'image') { el.stroke = 'transparent'; el.fill = 'transparent'; }
  if (type === 'symbol') {
    el.kind = props.kind || 'fcf';
    el.params = { ...(DEFAULT_PARAMS[el.kind] || {}), ...(props.params || {}) };
    el.fontSize = props.fontSize || style.symbolSize || 16;
    el.stroke = style.stroke || '#1e293b'; el.fill = 'transparent'; el.dash = 'solid';
    el.width = round2(Math.max(0.75, el.fontSize * 0.085));
    if (el.kind === 'dimension') { el.h = 0; } else { const sz = symbolSize(el); el.w = round2(sz.w); el.h = round2(sz.h); }
    if (!props.leader) { const l = defaultLeader(el); if (l) el.leader = l; else delete el.leader; }
  }
  return el;
}
function defaultLeader(el) {
  const s = el.fontSize;
  switch (el.kind) {
    case 'datum': return [round2(el.w / 2), round2(el.h + s * 2.6)];
    case 'balloon': return [round2(-el.w * 1.2), round2(el.h + el.w * 0.9)];
    case 'fcf': return [round2(-s * 2.6), round2(el.h / 2 + s * 2.6)];
    case 'weld': { const lay = layoutSymbol(el); return [round2(-s * 2.6), round2(lay.anchor[1] + s * 3)]; }
    default: return null;
  }
}

// After its points change: the box hugs the points again, nothing moves on the board.
export function normalizeLinear(el) {
  if (!LINEAR.has(el.type) || !el.points?.length) return el;
  const b = boxOfPoints(el.points);
  const abs = el.points.map((p) => [el.x + p[0], el.y + p[1], p[2]]);
  const c0 = center(el);
  const c1 = [el.x + b.x + b.w / 2, el.y + b.y + b.h / 2];
  const c1w = el.angle ? rotate(c1[0], c1[1], c0[0], c0[1], el.angle) : c1;
  const shiftX = c1w[0] - c1[0]; const shiftY = c1w[1] - c1[1];
  const nx = el.x + b.x + shiftX; const ny = el.y + b.y + shiftY;
  const points = abs.map((p) => {
    const q = [round2(p[0] + shiftX - nx), round2(p[1] + shiftY - ny)];
    if (p[2] != null) q.push(p[2]);
    return q;
  });
  return { ...el, x: round2(nx), y: round2(ny), w: round2(b.w), h: round2(b.h), points };
}
const round2 = (v) => Math.round(v * 100) / 100;

// A text element's box follows its words (unless it wraps at a set width).
export function fitText(el) {
  if (el.type !== 'text') return el;
  const lay = layoutText(el.text || ' ', { size: el.fontSize, font: el.font, bold: el.bold, italic: el.italic, maxWidth: el.wrap ? el.w : null });
  const w = el.wrap ? el.w : Math.max(el.fontSize * 0.6, lay.width + 2);
  const h = Math.max(lay.height, el.fontSize * 1.25);
  if (Math.abs(w - el.w) < 0.01 && Math.abs(h - el.h) < 0.01) return el;
  // keep the top-left corner where it was on the board (rotation included)
  const [tlx, tly] = toWorld(el, el.x, el.y);
  const nextCx = tlx + (w / 2) * Math.cos(el.angle || 0) - (h / 2) * Math.sin(el.angle || 0);
  const nextCy = tly + (w / 2) * Math.sin(el.angle || 0) + (h / 2) * Math.cos(el.angle || 0);
  return { ...el, w: round2(w), h: round2(h), x: round2(nextCx - w / 2), y: round2(nextCy - h / 2) };
}
// An annotation's box follows its content (its words, its compartments).
export function fitSymbol(el) {
  if (el.type !== 'symbol' || isDim(el)) return el;
  const sz = symbolSize(el);
  if (Math.abs(sz.w - el.w) < 0.01 && Math.abs(sz.h - el.h) < 0.01) return el;
  const [tlx, tly] = toWorld(el, el.x, el.y);
  const a = el.angle || 0; const w = sz.w; const h = sz.h;
  const cx = tlx + (w / 2) * Math.cos(a) - (h / 2) * Math.sin(a);
  const cy = tly + (w / 2) * Math.sin(a) + (h / 2) * Math.cos(a);
  return { ...el, w: round2(w), h: round2(h), x: round2(cx - w / 2), y: round2(cy - h / 2) };
}
// Scale an annotation by a corner: its words, frame and leader grow together;
// the opposite corner stays where it is on the board.
export function scaleSymbol(orig, handle, px, py) {
  const b = bodyBox(orig);
  const [lx, ly] = toLocal(orig, px, py);
  const ax = handle.includes('w') ? b.x + b.w : b.x; const ay = handle.includes('n') ? b.y + b.h : b.y;
  const hx = handle.includes('w') ? b.x : b.x + b.w; const hy = handle.includes('n') ? b.y : b.y + b.h;
  const k = Math.max(0.1, Math.min(20, Math.max(Math.abs(lx - ax) / Math.max(1, Math.abs(hx - ax)), Math.abs(ly - ay) / Math.max(1, Math.abs(hy - ay)))));
  const anchorWorld = toWorld(orig, ax, ay);
  const el = { ...orig, fontSize: round2(Math.min(400, Math.max(4, orig.fontSize * k))) };
  const kk = el.fontSize / orig.fontSize;
  el.width = round2(Math.max(0.5, orig.width * kk));
  if (orig.leader) el.leader = [round2(orig.leader[0] * kk), round2(orig.leader[1] * kk)];
  if (isDim(orig)) { el.w = round2(orig.w * kk); el.params = { ...orig.params, offset: round2((orig.params?.offset ?? -40) * kk) }; } else {
    const sz = symbolSize(el); el.w = round2(sz.w); el.h = round2(sz.h);
  }
  const nb = bodyBox({ ...el, x: 0, y: 0 });
  const relAx = handle.includes('w') ? nb.x + nb.w : nb.x; const relAy = handle.includes('n') ? nb.y + nb.h : nb.y;
  const [rx, ry] = rotate(relAx - el.w / 2, relAy - el.h / 2, 0, 0, el.angle || 0);
  const cx = anchorWorld[0] - rx; const cy = anchorWorld[1] - ry;
  return { ...el, x: round2(cx - el.w / 2), y: round2(cy - el.h / 2) };
}
// A dimension's two measured points (on the board), and dragging one.
export function dimEnds(el) { return [toWorld(el, el.x, el.y), toWorld(el, el.x + el.w, el.y)]; }
export function dimMoveEnd(el, which, px, py) {
  let [p0, p1] = dimEnds(el);
  if (which === 0) p0 = [px, py]; else p1 = [px, py];
  const L = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]);
  const a = Math.atan2(p1[1] - p0[1], p1[0] - p0[0]);
  const cx = (p0[0] + p1[0]) / 2; const cy = (p0[1] + p1[1]) / 2;
  return { ...el, x: round2(cx - L / 2), y: round2(cy), w: round2(L), h: 0, angle: normAngle(a) };
}
// Where the dimension line sits (the offset handle), and moving it.
export function dimOffsetPoint(el) { return toWorld(el, el.x + el.w / 2, el.y + (el.params?.offset ?? -40)); }
export function dimSetOffset(el, px, py) {
  const [, ly] = toLocal(el, px, py);
  let o = ly - el.y; if (Math.abs(o) < el.fontSize) o = Math.sign(o || -1) * el.fontSize;
  return { ...el, params: { ...el.params, offset: round2(o) } };
}
// An annotation's leader end on the board, and dragging it.
export function leaderEnd(el) { return el.leader ? toWorld(el, el.x + el.leader[0], el.y + el.leader[1]) : null; }
export function moveLeader(el, px, py) {
  const [lx, ly] = toLocal(el, px, py);
  return { ...el, leader: [round2(lx - el.x), round2(ly - el.y)] };
}

// A sticky note grows to hold its words (never shrinks below square).
export function fitNote(el) {
  if (el.type !== 'note') return el;
  const t = textBox({ ...el, h: 1e6 });
  const lay = layoutText(el.text || '', { size: el.fontSize, font: el.font, bold: el.bold, italic: el.italic, maxWidth: t.w });
  const need = lay.height + 2 * notePad(el);
  const h = Math.max(el.h, need);
  return Math.abs(h - el.h) < 0.5 ? el : { ...el, h: round2(h) };
}

// ---- moving, resizing, rotating -----------------------------------------------------
export const moveBy = (el, dx, dy) => ({ ...el, x: round2(el.x + dx), y: round2(el.y + dy) });

export function rotateAbout(el, cx, cy, da) {
  const [ex, ey] = center(el);
  const [nx, ny] = rotate(ex, ey, cx, cy, da);
  return { ...el, x: round2(nx - el.w / 2), y: round2(ny - el.h / 2), angle: normAngle((el.angle || 0) + da) };
}

// Resize ONE element by a handle ('n','s','e','w','ne','nw','se','sw') to the
// board point (px, py). keepRatio / fromCenter as Shift / Alt.
export function resizeOne(orig, handle, px, py, { keepRatio = false, fromCenter = false } = {}) {
  if (orig.type === 'symbol') return scaleSymbol(orig, handle.length === 2 ? handle : 'se', px, py);
  const [lx, ly] = toLocal(orig, px, py);
  let left = orig.x; let right = orig.x + orig.w; let top = orig.y; let bottom = orig.y + orig.h;
  const cx = orig.x + orig.w / 2; const cy = orig.y + orig.h / 2;
  if (handle.includes('w')) left = lx;
  if (handle.includes('e')) right = lx;
  if (handle.includes('n')) top = ly;
  if (handle.includes('s')) bottom = ly;
  if (fromCenter) {
    if (handle.includes('w')) right = 2 * cx - left;
    if (handle.includes('e')) left = 2 * cx - right;
    if (handle.includes('n')) bottom = 2 * cy - top;
    if (handle.includes('s')) top = 2 * cy - bottom;
  }
  if (keepRatio && orig.w > 0 && orig.h > 0) {
    const ratio = orig.w / orig.h;
    const corner = handle.length === 2;
    let w = right - left; let h = bottom - top;
    if (corner) {
      if (Math.abs(w) / ratio > Math.abs(h)) h = Math.sign(h || 1) * Math.abs(w) / ratio;
      else w = Math.sign(w || 1) * Math.abs(h) * ratio;
      if (fromCenter) { left = cx - w / 2; right = cx + w / 2; top = cy - h / 2; bottom = cy + h / 2; } else {
        if (handle.includes('w')) left = right - w; else right = left + w;
        if (handle.includes('n')) top = bottom - h; else bottom = top + h;
      }
    } else if (handle === 'e' || handle === 'w') {
      h = Math.abs(w) / ratio; top = cy - h / 2; bottom = cy + h / 2;
    } else {
      w = Math.abs(h) * ratio; left = cx - w / 2; right = cx + w / 2;
    }
  }
  const minSize = orig.type === 'text' ? orig.fontSize * 0.6 : 1;
  const flipX = right < left; const flipY = bottom < top;
  const nx = Math.min(left, right); const ny = Math.min(top, bottom);
  let nw = Math.abs(right - left); let nh = Math.abs(bottom - top);
  if (LINEAR.has(orig.type)) {
    if (orig.w === 0) nw = 0;
    if (orig.h === 0) nh = 0;
  } else { nw = Math.max(nw, minSize); nh = Math.max(nh, minSize); }
  // the new box centre, back in the board's frame
  const c1 = [nx + nw / 2, ny + nh / 2];
  const c1w = orig.angle ? rotate(c1[0], c1[1], cx, cy, orig.angle) : c1;
  let el = { ...orig, x: round2(c1w[0] - nw / 2), y: round2(c1w[1] - nh / 2), w: round2(nw), h: round2(nh) };
  if (LINEAR.has(orig.type)) {
    const sx = orig.w ? nw / orig.w : 1; const sy = orig.h ? nh / orig.h : 1;
    el.points = orig.points.map((p) => {
      const qx = flipX ? orig.w - p[0] : p[0]; const qy = flipY ? orig.h - p[1] : p[1];
      const q = [round2(qx * sx), round2(qy * sy)];
      if (p[2] != null) q.push(p[2]);
      return q;
    });
    if (orig.type === 'pen' || orig.type === 'highlighter') {
      const s = Math.sqrt(Math.max(0.0001, (orig.w ? sx : sy) * (orig.h ? sy : sx)));
      el.width = round2(Math.max(0.5, Math.min(400, orig.width * Math.min(4, Math.max(0.25, s)))));
    }
  }
  if (orig.type === 'text') {
    if (handle.length === 2) {
      // a corner scales the words
      const s = orig.h ? nh / orig.h : 1;
      el.fontSize = round2(Math.max(4, Math.min(800, orig.fontSize * s)));
      el = fitText({ ...el, wrap: orig.wrap, w: orig.wrap ? nw : el.w });
    } else if (handle === 'e' || handle === 'w') {
      el = fitText({ ...el, wrap: true, w: Math.max(orig.fontSize, nw) });
    } else el = fitText(el);
  }
  return el;
}

// Scale an element that is part of a bigger selection: anchor (ax, ay) stays,
// everything else stretches by (sx, sy) on the board's axes.
export function scaleWithin(el, ax, ay, sx, sy) {
  const [cx, cy] = center(el);
  const ncx = ax + (cx - ax) * sx; const ncy = ay + (cy - ay) * sy;
  const asx = Math.abs(sx); const asy = Math.abs(sy);
  // a rotated element keeps its angle; the stretch lands on its own axes
  const cos = Math.abs(Math.cos(el.angle || 0)); const sin = Math.abs(Math.sin(el.angle || 0));
  const lsx = asx * cos + asy * sin; const lsy = asy * cos + asx * sin;
  const nw = el.w * lsx; const nh = el.h * lsy;
  if (el.type === 'symbol') {
    const k = Math.sqrt(asx * asy);
    const s2 = { ...el, fontSize: round2(Math.min(400, Math.max(4, el.fontSize * k))), width: round2(Math.max(0.5, el.width * k)) };
    if (el.leader) s2.leader = [round2(el.leader[0] * k), round2(el.leader[1] * k)];
    if (isDim(el)) { s2.w = round2(el.w * k); s2.h = 0; s2.params = { ...el.params, offset: round2((el.params?.offset ?? -40) * k) }; } else {
      const sz = symbolSize(s2); s2.w = round2(sz.w); s2.h = round2(sz.h);
    }
    return { ...s2, x: round2(ncx - s2.w / 2), y: round2(ncy - s2.h / 2) };
  }
  const out = { ...el, x: round2(ncx - nw / 2), y: round2(ncy - nh / 2), w: round2(nw), h: round2(nh) };
  if (LINEAR.has(el.type)) {
    out.points = el.points.map((p) => {
      const qx = sx < 0 ? el.w - p[0] : p[0]; const qy = sy < 0 ? el.h - p[1] : p[1];
      const q = [round2(qx * lsx), round2(qy * lsy)];
      if (p[2] != null) q.push(p[2]);
      return q;
    });
    if (el.type === 'pen' || el.type === 'highlighter') out.width = round2(Math.max(0.5, Math.min(400, el.width * Math.sqrt(lsx * lsy))));
  }
  if (HAS_TEXT.has(el.type) && el.fontSize) {
    out.fontSize = round2(Math.max(4, Math.min(800, el.fontSize * Math.sqrt(lsx * lsy))));
    if (el.type === 'text') return fitText(out);
  }
  return out;
}

// Drag one end (or a bend) of a line / arrow to the board point (px, py).
export function movePoint(el, index, px, py) {
  const [lx, ly] = toLocal(el, px, py);
  const points = el.points.map((p, i) => (i === index ? [round2(lx - el.x), round2(ly - el.y)] : p));
  return normalizeLinear({ ...el, points });
}

// ---- what an element looks like ----------------------------------------------------
// prims: { k: 'path', ops, fill, stroke, width, dash, cap, join, shadow }
//        { k: 'text', lines: [{ text, x, y }], size, font, bold, italic, color, align }
//        { k: 'image', fileId, x, y, w, h }
// + the element's own { angle, cx, cy, opacity } for the renderer to apply.
export function headSize(el) { return Math.max(10, el.width * 3.6); }
function rectOps(x, y, w, h, r) {
  if (!r) return [['M', x, y], ['L', x + w, y], ['L', x + w, y + h], ['L', x, y + h], ['Z']];
  const k = r * (1 - K);
  return [
    ['M', x + r, y], ['L', x + w - r, y], ['C', x + w - k, y, x + w, y + k, x + w, y + r],
    ['L', x + w, y + h - r], ['C', x + w, y + h - k, x + w - k, y + h, x + w - r, y + h],
    ['L', x + r, y + h], ['C', x + k, y + h, x, y + h - k, x, y + h - r],
    ['L', x, y + r], ['C', x, y + k, x + k, y, x + r, y], ['Z'],
  ];
}
function ellipseOps(x, y, w, h) {
  const rx = w / 2; const ry = h / 2; const cx = x + rx; const cy = y + ry;
  const ox = rx * K; const oy = ry * K;
  return [
    ['M', cx + rx, cy],
    ['C', cx + rx, cy + oy, cx + ox, cy + ry, cx, cy + ry],
    ['C', cx - ox, cy + ry, cx - rx, cy + oy, cx - rx, cy],
    ['C', cx - rx, cy - oy, cx - ox, cy - ry, cx, cy - ry],
    ['C', cx + ox, cy - ry, cx + rx, cy - oy, cx + rx, cy],
    ['Z'],
  ];
}
const polyOps = (pts) => [['M', pts[0][0], pts[0][1]], ...pts.slice(1).map((p) => ['L', p[0], p[1]]), ['Z']];

function headPrims(el, tip, from, kind) {
  if (!kind || kind === 'none') return [];
  const s = headSize(el);
  const ang = Math.atan2(tip[1] - from[1], tip[0] - from[0]);
  const at = (a, d) => [tip[0] - Math.cos(a) * d, tip[1] - Math.sin(a) * d];
  const style = { stroke: el.stroke, width: el.width, cap: 'round', join: 'round' };
  if (kind === 'arrow') {
    const a = at(ang - 0.5, s); const b = at(ang + 0.5, s);
    return [{ k: 'path', ops: [['M', a[0], a[1]], ['L', tip[0], tip[1]], ['L', b[0], b[1]]], ...style }];
  }
  if (kind === 'triangle') {
    const a = at(ang - 0.42, s); const b = at(ang + 0.42, s);
    return [{ k: 'path', ops: polyOps([tip, a, b]), fill: el.stroke, ...style }];
  }
  if (kind === 'dot') {
    const r = Math.max(3, el.width * 1.6);
    return [{ k: 'path', ops: ellipseOps(tip[0] - r, tip[1] - r, r * 2, r * 2), fill: el.stroke, ...style }];
  }
  if (kind === 'bar') {
    const p = ang + Math.PI / 2; const d = s * 0.6;
    return [{ k: 'path', ops: [['M', tip[0] + Math.cos(p) * d, tip[1] + Math.sin(p) * d], ['L', tip[0] - Math.cos(p) * d, tip[1] - Math.sin(p) * d]], ...style }];
  }
  return [];
}

const notePad = (el) => Math.max(10, el.fontSize * 0.7);
// The box words sit in, inside a shape (absolute, unrotated).
export function textBox(el) {
  const { x, y, w, h } = el;
  if (el.type === 'text') return { x, y, w, h };
  if (el.type === 'note') { const p = notePad(el); return { x: x + p, y: y + p, w: Math.max(1, w - 2 * p), h: Math.max(1, h - 2 * p) }; }
  if (el.type === 'ellipse') return { x: x + w * 0.15, y: y + h * 0.15, w: w * 0.7, h: h * 0.7 };
  if (el.type === 'diamond') return { x: x + w * 0.25, y: y + h * 0.25, w: w * 0.5, h: h * 0.5 };
  if (el.type === 'triangle') return { x: x + w * 0.25, y: y + h * 0.45, w: w * 0.5, h: h * 0.5 };
  const p = Math.max(6, el.width + 4);
  return { x: x + p, y: y + p, w: Math.max(1, w - 2 * p), h: Math.max(1, h - 2 * p) };
}
// Lay the words out in their box → the text primitive (or null).
export function textPrim(el) {
  if (!HAS_TEXT.has(el.type) || !el.text) return null;
  const box = textBox(el);
  const size = el.fontSize;
  const lay = layoutText(el.text, { size, font: el.font, bold: el.bold, italic: el.italic, maxWidth: el.type === 'text' && !el.wrap ? null : box.w });
  const lh = size * 1.25;
  const top = el.type === 'text' ? box.y : box.y + (box.h - lay.height) / 2;
  const lines = lay.lines.map((l, i) => {
    let lx = box.x;
    if (el.align === 'center') lx = box.x + (box.w - l.width) / 2;
    else if (el.align === 'right') lx = box.x + box.w - l.width;
    return { text: l.text, x: lx, y: top + i * lh + baselineOffset(size), width: l.width };
  });
  return { k: 'text', lines, size, font: el.font, bold: !!el.bold, italic: !!el.italic, color: el.textColor || '#1e293b', align: el.align };
}

const primCache = new WeakMap();
export function primitives(el) {
  const hit = primCache.get(el);
  if (hit) return hit;
  const prims = [];
  const ink = { stroke: el.stroke, width: el.width, dash: el.dash, cap: 'round', join: 'round' };
  const hasStroke = el.stroke && el.stroke !== 'transparent';
  const hasFill = el.fill && el.fill !== 'transparent';
  switch (el.type) {
    case 'pen': {
      const pts = el.points.map((p) => [el.x + p[0], el.y + p[1], p[2]]);
      prims.push({ k: 'path', ops: smoothClosed(strokeOutline(pts, el.width, { pen: el.pen === true })), fill: el.stroke });
      break;
    }
    case 'highlighter': {
      const pts = el.points.map((p) => [el.x + p[0], el.y + p[1]]);
      prims.push({ k: 'path', ops: smoothOpen(pts), stroke: el.stroke, width: el.width, cap: 'round', join: 'round' });
      break;
    }
    case 'line': case 'arrow': {
      const pts = el.points.map((p) => [el.x + p[0], el.y + p[1]]);
      prims.push({ k: 'path', ops: [['M', pts[0][0], pts[0][1]], ...pts.slice(1).map((p) => ['L', p[0], p[1]])], ...ink });
      if (pts.length > 1) {
        prims.push(...headPrims(el, pts[pts.length - 1], pts[pts.length - 2], el.endHead));
        prims.push(...headPrims(el, pts[0], pts[1], el.startHead));
      }
      break;
    }
    case 'rect':
      prims.push({ k: 'path', ops: rectOps(el.x, el.y, el.w, el.h, el.round ? Math.min(24, Math.min(el.w, el.h) * 0.15) : 0), fill: hasFill ? el.fill : null, ...(hasStroke ? ink : { stroke: null }) });
      break;
    case 'ellipse':
      prims.push({ k: 'path', ops: ellipseOps(el.x, el.y, el.w, el.h), fill: hasFill ? el.fill : null, ...(hasStroke ? ink : { stroke: null }) });
      break;
    case 'diamond': case 'triangle':
      prims.push({ k: 'path', ops: polyOps(outlinePolygon(el)), fill: hasFill ? el.fill : null, ...(hasStroke ? ink : { stroke: null }) });
      break;
    case 'note':
      prims.push({ k: 'path', ops: rectOps(el.x, el.y, el.w, el.h, el.round ? 10 : 2), fill: el.fill, stroke: null, shadow: true });
      break;
    case 'image':
      prims.push({ k: 'image', fileId: el.fileId, x: el.x, y: el.y, w: el.w, h: el.h });
      break;
    case 'symbol':
      // a dimension read upside down turns its words the right way up
      prims.push(...symbolPrims(el, isDim(el) && Math.abs(normAngle(el.angle || 0)) > Math.PI / 2 + 1e-6));
      break;
    default: break;
  }
  const t = textPrim(el);
  if (t) prims.push(t);
  const [cx, cy] = center(el);
  const out = { prims, angle: el.angle || 0, cx, cy, opacity: el.opacity ?? 1 };
  primCache.set(el, out);
  return out;
}

// Far zoomed out, a pen stroke's pressure cannot be seen: the screen draws
// it as a plain line of its width through fewer points (much less to paint).
const lodCache = new WeakMap();
export function penLodPath(el) {
  let d = lodCache.get(el);
  if (d) return d;
  const pts = el.points.map((p) => [el.x + p[0], el.y + p[1]]);
  const kept = pts.length > 3 ? simplifyPts(pts, Math.max(1, el.width * 0.6)) : pts;
  d = opsToSvg([['M', kept[0][0], kept[0][1]], ...(kept.length > 1 ? kept.slice(1) : [[kept[0][0] + 0.01, kept[0][1]]]).map((p) => ['L', p[0], p[1]])]);
  lodCache.set(el, d);
  return d;
}

// Dash patterns in board units for a stroke width.
export function dashArray(dash, width) {
  if (dash === 'dashed') return [width * 3 + 6, width * 2.4 + 6];
  if (dash === 'dotted') return [0.01, width * 2 + 4];
  return null;
}

// Path ops → an SVG path string.
export function opsToSvg(ops) {
  const f = (v) => (Math.round(v * 100) / 100).toString();
  let s = '';
  for (const op of ops) {
    if (op[0] === 'Z') s += 'Z';
    else s += op[0] + op.slice(1).map(f).join(' ');
  }
  return s;
}

// Order: lowest z first; ties by id so every screen agrees.
export function byZ(a, b) { return a.z - b.z || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0); }

// ---- drawings: a work item's PDF page or picture brought onto the board ------------------
// A drawing carries its document (`doc`); one placed before that was kept
// knows itself by its file id (the server names those `at…` / `pg…` + 22 hex).
const DRAWING_FILE = /^(at|pg)[0-9a-f]{22}$/;
export function isDrawing(el) {
  return !!el && el.type === 'image' && !el.deleted && (!!el.doc || DRAWING_FILE.test(el.fileId || ''));
}
// The markup on drawings: whatever sits with its middle on one of them and
// travels with it (not another drawing, nothing locked in place).
export function ridersOf(elements, drawings) {
  const frames = drawings.map((d) => bounds(d)).filter(Boolean);
  const ids = new Set(drawings.map((d) => d.id));
  if (!frames.length) return [];
  return elements.filter((e) => {
    if (e.deleted || ids.has(e.id) || e.locked || isDrawing(e)) return false;
    const b = bounds(e); if (!b) return false;
    const cx = b.x + b.w / 2; const cy = b.y + b.h / 2;
    return frames.some((f) => cx >= f.x && cx <= f.x + f.w && cy >= f.y && cy <= f.y + f.h);
  });
}
